import { useContext, useRef, type SyntheticEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, generatePath } from 'react-router-dom';
import { useDrop } from 'react-dnd';
import { useIntl } from 'react-intl';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import type { TabProps } from '@mui/material/Tab';
import { styled, alpha } from '@mui/material/styles';
import { pipe, Array as A } from 'effect';
import cx from 'classnames';

import { brainCategorizationEnabledSelector, documentsChangeTypesAction } from 'domain/documents';
import { getNestedCategory, findCatById } from 'domain/categories/helpers';
import { doctypeTabLookupSelector, categoriesListSelector } from 'domain/categories/selectors';
import { DOCUMENT } from 'components/DnDItem';
import CONST from 'domain/documents/constants';
import toast from 'components/Toast';
import CompanyWorkspaceContext from 'pages/company/WorkspaceRoute/CompanyWorkspaceContext';
import { useWorkspaceDoctypes } from 'pages/company/WorkspaceRoute/hooks/useWorkspaceAggregations';
import ROUTES_PATH from 'domain/router/routesPathConfig';

// Routed doctype tab bar (spec da-19295 D7). Active tab = deepest URL category segment matched
// against each doctype's routing `id`; on the bare category that segment equals the All tab's `id`,
// so All is active by default (appends no segment). Fin/Gen append their `id` as the
// next-deeper segment. The Financial/General tabs are also reclassify drop targets (task 4.1):
// dropping a document — or the current multi-selection — writes _T_FIN / _T_GEN. All is not a target.

const StyledTabs = styled(Tabs, { label: 'WorkspaceDoctypeTabs' })(({ theme }) => ({
  minHeight: 40,
  // Spacing lives here, not as a bottom margin on CompanyBarPanel: this component renders null for
  // non-brain companies, so a parent-side margin would leave a dead gap. WorkspaceSubtypeChips
  // carries its own top padding, so the chips-without-tabs case is already covered.
  marginTop: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main },
}));

const StyledTab = styled(Tab, { label: 'WorkspaceDoctypeTab' })(({ theme }) => ({
  minHeight: 40,
  color: theme.palette.text.secondary,
  '&.Mui-selected': { color: theme.palette.primary.main, fontWeight: theme.typography.fontWeightBold },
  '&.is-drag-over': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    outline: `1px dashed ${theme.palette.primary.main}`,
    outlineOffset: -1,
  },
}));

// Financial/General tabs are reclassify drop targets (spec 4.1). `useDrop` on the Tab ref keeps
// it a direct <Tabs> child (indicator/value intact). The All tab is not a target. Always a bulk
// write over the current selection (or the single dragged doc when nothing is selected).
const TAB_WRITE: Record<string, { type: string; toastId: string; toastMsg: string }> = {
  financial: { type: CONST.tags.financial, toastId: 'document.actions.financial.success', toastMsg: 'Document marked as financial' },
  general: { type: CONST.tags.general, toastId: 'document.actions.general.success', toastMsg: 'Document marked as general' },
};

type DroppableTabProps = TabProps & { writeType: string; toastId: string; toastMsg: string; selectedIds: string[] };

const DroppableTab = ({ writeType, toastId, toastMsg, selectedIds, ...tabProps }: DroppableTabProps) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  // The drag item comes from the legacy DnDItemSource (components/DnDItem); its runtime shape is
  // `{ id, ...params, itemType }` and we read `id`. `type` is not on the item — it is here only
  // because react-dnd v7's useDrop generic must extend DragObjectWithType ({ type }).
  const [{ isOver, canDrop }, drop] = useDrop<{ type: string; id: string }, unknown, { isOver: boolean; canDrop: boolean }>({
    accept: DOCUMENT,
    drop: (item) => {
      if (!item?.id) return;
      const documentsIds = selectedIds.length ? selectedIds : [item.id];
      new Promise((resolve, reject) => {
        dispatch({ type: documentsChangeTypesAction.type, payload: { documentsIds, type: writeType, resolve, reject } });
      })
        .then(() => toast.info(formatMessage({ id: toastId, defaultMessage: toastMsg })))
        .catch(() => null);
    },
    collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
  });
  drop(ref);
  return <StyledTab ref={ref} {...tabProps} className={cx({ 'is-drag-over': isOver && canDrop })} />;
};

const WorkspaceDoctypeTabs = ({ selectedIds = [] }: { selectedIds?: string[] }) => {
  const enabled = useSelector(brainCategorizationEnabledSelector);
  const doctypeLookup = useSelector(doctypeTabLookupSelector);
  const categoriesList = useSelector(categoriesListSelector);
  const doctypes = useWorkspaceDoctypes();
  const { companyId, category1, category2, category3 } = useParams();
  const navigate = useNavigate();
  const { companyType } = useContext(CompanyWorkspaceContext);

  // To-Approve (and any non-eligible category) is suppressed by the backend returning no tabs.
  if (!enabled || doctypes.length === 0) return null;

  // Active tab is derived from the stable all-categories catalog (loaded with the sidebar), NOT the
  // per-category aggregations, which lag one navigation behind: on navigation `deepest` flips to the
  // destination's tab id instantly while `doctypes` still holds the previous category's tabs, so a
  // `doctypes`-based match briefly falls back to All and the indicator animates All→Financial. The
  // catalog resolves the deepest segment's name_id immediately, so the active tab is correct at once.
  const deepest = getNestedCategory({ category1, category2, category3 });
  const value = doctypeLookup.get(String(deepest)) ?? 'all';

  const template =
    companyType === 'confidential'
      ? ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.absolute
      : ROUTES_PATH.COMPANY_WORKSPACE.absolute;

  const handleChange = (_event: SyntheticEvent, nextName: string) => {
    const filled = pipe(
      [category1, category2, category3],
      A.filter((s): s is string => Boolean(s)),
    );
    // Strip the deepest slot when it is a doctype tab (Fin/Gen), and resolve the clicked tab's
    // routing id — both from the stable catalog (URL lookup + the current category's `tabs`), NOT the
    // aggregations `doctypes`, which lag one navigation behind and would yield the previous category's
    // per-tab ids on a fast click right after navigating.
    const deepestName = doctypeLookup.get(String(deepest));
    const onDoctypeSegment = deepestName === CONST.doctype.financial || deepestName === CONST.doctype.general;
    const base = onDoctypeSegment ? filled.slice(0, -1) : filled;
    const currentCat = findCatById(categoriesList, base[base.length - 1]);
    const nextId = currentCat?.tabs?.find((t: { name_id: string; id: string | number }) => t.name_id === nextName)?.id;
    const next = nextName === 'all' || nextId == null ? base : [...base, String(nextId)];
    const [nextCat1, nextCat2, nextCat3] = next;

    navigate(
      generatePath(template, {
        companyId,
        category1: nextCat1,
        category2: nextCat2,
        category3: nextCat3,
      }),
    );
  };

  return (
    <StyledTabs value={value} onChange={handleChange}>
      {doctypes.map(({ name_id, label, count }) =>
        TAB_WRITE[name_id] ? (
          <DroppableTab
            key={name_id}
            value={name_id}
            label={`${label} (${count})`}
            selectedIds={selectedIds}
            writeType={TAB_WRITE[name_id].type}
            toastId={TAB_WRITE[name_id].toastId}
            toastMsg={TAB_WRITE[name_id].toastMsg}
          />
        ) : (
          <StyledTab key={name_id} value={name_id} label={`${label} (${count})`} />
        ),
      )}
    </StyledTabs>
  );
};

export default WorkspaceDoctypeTabs;
